/**
 * @voltxampere/web — `<vxa-bode-plot>` AC Bode plot panel (Sprint 20)
 *
 * Subscribes to the `currentACResult` signal store, reads the
 * channel→net binding from the existing probe-binding state, and
 * renders the Sprint 20 D-20-6 Bode layout (magnitude + phase
 * stacked, log-frequency X-axis) onto a Canvas2D surface.
 *
 * Sprint 20 keeps this component intentionally narrow:
 *  - Read-only display; settings (linear/dB, deg/rad) live on the
 *    component element via attributes for now. Sprint 21+ will add
 *    an in-component toolbar.
 *  - No cursor / measurement readouts. Sprint 21 will share
 *    cursor logic with `<vxa-waveform>`.
 *  - F6-driven; the empty state instructs users to press F6.
 *  - Ne `<vxa-waveform>` ne pane router'a entegre — Şefim tetiklediği
 *    zaman simulator pane'inde manuel mount edilebilir; layout
 *    integration Sprint 21'de.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, css, html, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";

import { bodeChannelColor, renderBodeFrame, type BodeTrace } from "@voltxampere/ui/waveform";

import { currentACResult } from "../store/ac-runner.js";
import { probeBindings } from "../store/probes.js";
import { channels } from "../store/channels.js";
import { t } from "../i18n/index.js";

@customElement("vxa-bode-plot")
export class VxaBodePlot extends LitElement {
  static override styles = css`
    :host {
      display: block;
      position: relative;
      background: rgba(8, 12, 23, 0.92);
      border: 1px solid rgba(180, 180, 200, 0.18);
      border-radius: 6px;
      overflow: hidden;
      min-height: 240px;
    }
    canvas {
      display: block;
      width: 100%;
      height: 100%;
    }
    /* Sprint 24 A2 — Bode UX overlay: sub-plot başlıkları (üst-sol),
       axis labels (alt-merkez + yan-rotate), legend chips (üst-sağ).
       Canvas üzerine HTML overlay; pointer-events: none ile canvas
       interaksiyonunu engellemiyor. */
    .subplot-titles {
      position: absolute;
      top: 8px;
      left: 12px;
      display: flex;
      flex-direction: column;
      gap: 110px;
      pointer-events: none;
      font-family: "Cormorant Infant", "EB Garamond", serif;
      color: rgba(243, 234, 213, 0.72);
      font-size: 0.85rem;
      font-style: italic;
    }
    .subplot-titles .mag {
      color: var(--brass, #c08a3e);
    }
    .legend-chips {
      position: absolute;
      top: 8px;
      right: 12px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      pointer-events: none;
      font-family: "JetBrains Mono", ui-monospace, monospace;
      font-size: 0.65rem;
    }
    .legend-chips .chip {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 2px 8px;
      background: rgba(8, 12, 23, 0.85);
      border: 1px solid rgba(192, 138, 62, 0.35);
      border-radius: 999px;
      letter-spacing: 0.08em;
      color: rgba(243, 234, 213, 0.85);
    }
    .legend-chips .swatch {
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }
    .axis-x-label {
      position: absolute;
      bottom: 4px;
      left: 50%;
      transform: translateX(-50%);
      pointer-events: none;
      font-family: "JetBrains Mono", ui-monospace, monospace;
      font-size: 0.7rem;
      letter-spacing: 0.18em;
      color: rgba(243, 234, 213, 0.55);
      text-transform: uppercase;
    }
    .empty {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      color: rgba(220, 220, 230, 0.7);
      font-family: "Cormorant Infant", "EB Garamond", serif;
      pointer-events: none;
    }
    .empty .title {
      font-size: 1.3rem;
      letter-spacing: 0.04em;
    }
    .empty .hint {
      font-family: "JetBrains Mono", ui-monospace, monospace;
      font-size: 0.8rem;
      opacity: 0.7;
      margin-top: 0.25rem;
    }
  `;

  @property({ type: String, reflect: true }) magnitudeUnit: "linear" | "dB" = "linear";
  @property({ type: String, reflect: true }) phaseUnit: "deg" | "rad" = "deg";

  @state()
  private hasResult = false;

  private canvas: HTMLCanvasElement | null = null;
  private cleanup: (() => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.cleanup = effect(() => {
      // Subscribe to result, channels, probe bindings.
      const ac = currentACResult.value;
      this.hasResult = ac !== null && ac.frequencies.length > 0;
      // Defer to the next animation frame so the canvas is mounted.
      requestAnimationFrame(() => {
        this.renderFrame();
      });
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.cleanup?.();
    this.cleanup = null;
  }

  override updated(changed: PropertyValues<this>): void {
    super.updated(changed);
    if (changed.has("magnitudeUnit") || changed.has("phaseUnit")) {
      this.renderFrame();
    }
  }

  override render() {
    return html`
      <canvas></canvas>
      ${this.hasResult ? this.renderOverlay() : this.renderEmpty()}
    `;
  }

  private renderEmpty() {
    return html`
      <div class="empty">
        <div>
          <div class="title">${t("ac.empty.title")}</div>
          <div class="hint">${t("ac.empty.hint")}</div>
        </div>
      </div>
    `;
  }

  /**
   * Sprint 24 A2 — overlay (D-24-2 closure):
   * - Sub-plot titles (top-left): "|H(jω)|" + "∠H(jω)"
   * - Axis labels (bottom-center): "Frekans (Hz)"
   * - Legend chips (top-right): probe-bound channels
   *
   * Canvas zaten log-decade tick label'larını çiziyor (ngspice
   * canon 10/100/1k/10k/100k); HTML overlay ek içeriksel etiket
   * + lejant ekler.
   */
  private renderOverlay() {
    const bindings = probeBindings.value;
    const ch = channels.value;
    const legend: { id: string; label: string; color: string }[] = [];
    for (const [netId, channelId] of bindings.entries()) {
      const channelState = ch.get(channelId);
      if (!channelState?.enabled) continue;
      legend.push({
        id: channelId,
        label: `${channelId.toUpperCase()} · ${netId}`,
        color: bodeChannelColor(parseChannelOrder(channelId)),
      });
    }
    return html`
      <div class="subplot-titles">
        <span class="mag">${t("bode.subplot.mag")}</span>
        <span class="phase">${t("bode.subplot.phase")}</span>
      </div>
      ${legend.length > 0
        ? html`
            <div class="legend-chips" data-testid="bode-legend">
              ${legend.map(
                (l) => html`
                  <span class="chip">
                    <span class="swatch" style="background:${l.color}"></span>
                    <span>${l.label}</span>
                  </span>
                `,
              )}
            </div>
          `
        : null}
      <div class="axis-x-label" data-testid="bode-axis-x">${t("bode.axis.frequency")}</div>
    `;
  }

  override firstUpdated(): void {
    this.canvas = this.renderRoot.querySelector("canvas");
    this.renderFrame();
  }

  private renderFrame(): void {
    this.canvas ??= this.renderRoot.querySelector("canvas");
    const canvas = this.canvas;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const ac = currentACResult.value;
    const dpr = typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth || 480;
    const cssHeight = canvas.clientHeight || 240;
    canvas.width = Math.floor(cssWidth * dpr);
    canvas.height = Math.floor(cssHeight * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (!ac || ac.frequencies.length === 0) {
      ctx.clearRect(0, 0, cssWidth, cssHeight);
      return;
    }

    const traces: BodeTrace[] = [];
    const bindings = probeBindings.value;
    const ch = channels.value;
    for (const [netId, channelId] of bindings.entries()) {
      const trace = ac.nodeTraces.get(netId);
      if (!trace) continue;
      const channelState = ch.get(channelId);
      if (!channelState?.enabled) continue;
      traces.push({
        id: channelId,
        color: bodeChannelColor(parseChannelOrder(channelId)),
        magnitudes: trace.magnitudes,
        phases: trace.phases,
      });
    }

    // Fallback when no probe is bound: show all node traces with
    // implicit ordering. Lets the user see something on first F6
    // before configuring channels.
    if (traces.length === 0) {
      let i = 0;
      for (const [netId, trace] of ac.nodeTraces.entries()) {
        if (i >= 4) break;
        traces.push({
          id: netId,
          color: bodeChannelColor(i),
          magnitudes: trace.magnitudes,
          phases: trace.phases,
        });
        i++;
      }
    }

    renderBodeFrame(ctx, {
      width: cssWidth,
      height: cssHeight,
      frequencies: ac.frequencies,
      traces,
      magnitudeUnit: this.magnitudeUnit,
      phaseUnit: this.phaseUnit,
    });
  }
}

function parseChannelOrder(channelId: string): number {
  const match = /(\d)/.exec(channelId);
  if (!match) return 0;
  const n = Number.parseInt(match[1] ?? "1", 10);
  return Number.isFinite(n) ? Math.max(0, n - 1) : 0;
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-bode-plot": VxaBodePlot;
  }
}
